import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Package, Star } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import GoogleReviewBadge from './GoogleReviewBadge';

const fmtPrice = (price, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price || 0);

/** PackageCard — marketplace card for operator bundles ("Package · Save X%"). */
const PackageCard = ({ pkg, testId }) => {
  const navigate = useNavigate();
  if (!pkg) return null;

  const op = pkg.operator || {};
  const img = pkg.imageUrl || (pkg.images && pkg.images[0]);
  const pct = Math.round(pkg.discountPercent || 0);
  const hasReviews = op.reviewCount > 0 && op.rating;

  return (
    <Card
      onClick={() => navigate(`/package/${pkg.id}`)}
      className="group cursor-pointer border-0 rounded-3xl bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.10)] hover:-translate-y-1 transition-[box-shadow,transform] duration-300"
      data-testid={testId || `marketplace-package-card-${pkg.id}`}
    >
      <div className="aspect-[5/4] relative overflow-hidden rounded-2xl bg-gray-100">
        {img ? (
          <img
            src={img}
            alt={pkg.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-[#04091B] flex items-center justify-center">
            <Package className="w-10 h-10 text-slate-600" />
          </div>
        )}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[11px] font-bold text-gray-900">
          <Package className="w-3 h-3 text-blue-600" />
          Package
        </span>
        {pct > 0 && (
          <span className="absolute top-2 right-2 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-extrabold text-white" data-testid="package-card-save-badge">
            Save {pct}%
          </span>
        )}
      </div>

      <CardContent className="px-1.5 pt-3.5 pb-1.5">
        <div className="flex items-center justify-between gap-2 text-sm text-gray-500">
          <span className="flex items-center gap-1 min-w-0">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{op.city || 'Location TBD'}</span>
          </span>
          {op.googleReviews?.count ? (
            <GoogleReviewBadge reviews={op.googleReviews} />
          ) : hasReviews ? (
            <span className="inline-flex shrink-0 items-center gap-0.5 text-xs font-bold text-gray-900">
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              {Number(op.rating).toFixed(1)}
              <span className="font-medium text-gray-400">({op.reviewCount})</span>
            </span>
          ) : null}
        </div>

        <h3 className="mt-1.5 text-lg font-bold leading-snug text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {pkg.name}
        </h3>

        {op.name && <p className="mt-1 text-sm text-gray-500 truncate">by {op.name}</p>}
        <p className="mt-1 text-xs text-gray-400 line-clamp-1">
          {(pkg.services || []).map((s) => s.name).join(' + ')}
        </p>

        <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5">
          <span className="text-sm text-gray-500">{(pkg.services || []).length} experiences</span>
          <div className="shrink-0 text-right">
            {pct > 0 && (
              <span className="text-xs text-gray-400 line-through mr-1.5 tabular-nums">
                {fmtPrice(pkg.combinedFromPrice, pkg.currency)}
              </span>
            )}
            <span className="text-xl font-extrabold tabular-nums text-blue-600">
              {fmtPrice(pkg.packageFromPrice, pkg.currency)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PackageCard;
